<ng-template #commandFeedback>
  <div class="pdw-command" aria-live="polite">
    @if (busy()) {
      <p class="pdw-command__status">
        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
        Sending to the workflow engine…
      </p>
    }
  </div>
  @if (commandError(); as error) {
    <p class="gate__blocked" role="status">{{ error }}</p>
  }
</ng-template>

<ng-template #executeAction>
  @let perms = permissions();
  <div id="pdw-current-step" class="pdw-panel pdw-panel--action">
    <h4 class="pdw-panel__title">
      {{ stepLabel() }}
      <span class="pdw-panel__hint">{{ actionSummary() }} · one at a time</span>
    </h4>
    @if (activeItem(); as active) {
      <div class="pdw-active-item">
        <p class="pdw-active-item__label">Current action · {{ roleLabels[active.targetRole] }}</p>
        <dl class="pdw-active-item__facts">
          @if (activeCandidate(); as candidate) {
            <div>
              <dt>Why</dt>
              <dd>{{ candidate.rationale }}</dd>
            </div>
          }
          <div>
            <dt>Deviation</dt>
            <dd>{{ workflowCase().context.summary }}</dd>
          </div>
        </dl>
      </div>
      <h5 class="pdw-subtitle">
        Completion criteria
        <span class="pdw-panel__hint">{{ checkedCount() }} of {{ active.completionCriteria.length }} confirmed</span>
      </h5>
      <ul class="pdw-criteria">
        @for (criterion of active.completionCriteria; track criterion) {
          <li class="pdw-criteria__item">
            <label class="pdw-criteria__label">
              <input
                [checked]="checkedCriteria().includes(criterion)"
                [disabled]="!perms.canCompleteAction || busy()"
                (change)="toggleCriterion(criterion)"
                type="checkbox" />
              <span>{{ criterion }}</span>
            </label>
          </li>
        }
      </ul>
      @if (perms.canCompleteAction) {
        <div class="gate__field">
          <label class="gate__label" for="pdw-completion-note">Completion note <span class="gate__required">required</span></label>
          <textarea
            [formControl]="completionNote"
            [readOnly]="busy()"
            id="pdw-completion-note"
            [attr.aria-invalid]="exceptionBlockedReason() ? 'true' : null"
            [attr.aria-describedby]="exceptionBlockedReason() ? 'pdw-exception-error' : null"
            class="gate__control"
            rows="3"
            placeholder="What was reviewed or changed?"></textarea>
        </div>
        @if (hasExceptionOutcomes()) {
          <div class="gate__field">
            <label class="gate__label" for="pdw-action-reason">Reason <span class="gate__muted">(required for Not applicable or Cannot complete)</span></label>
            <textarea [formControl]="actionReason" [readOnly]="busy()" id="pdw-action-reason" class="gate__control"
              rows="3" placeholder="Why does this action not apply, or what prevents completing it?"></textarea>
          </div>
        }
        @if (actionOutcomes().includes('done') && completeBlockedReason(); as blocked) {
          <p class="gate__blocked" role="status">{{ blocked }}</p>
        }
        @if (exceptionBlockedReason(); as blocked) {
          <p id="pdw-exception-error" class="gate__blocked" role="alert">{{ blocked }}</p>
        }
        <div class="gate__actions">
          @if (actionOutcomes().includes('done')) {
            <button [disabled]="!canComplete() || busy()" (click)="onCompleteAction()"
              type="button" class="gate__btn gate__btn--primary">Mark complete</button>
          }
          @for (outcome of actionOutcomes(); track outcome) {
            @if (outcome !== 'done') {
              <button [disabled]="!hasActionReason() || busy()" (click)="onCompleteAction(outcome)"
                type="button" class="gate__btn gate__btn--primary">{{ actionStatusLabels[outcome] }}</button>
            }
          }
        </div>
      } @else {
        <p class="pdw-waiting">{{ waitingMessage() }}</p>
      }
    }
    <ng-container [ngTemplateOutlet]="commandFeedback" />
  </div>
</ng-template>

<ng-template #actionTable let-candidateId>
  <div class="pdw-table-wrap">
    <table class="pdw-table pdw-action-table" aria-label="Recommended actions">
      <colgroup>
        <col class="pdw-action-table__action" />
        <col class="pdw-action-table__role" />
        <col class="pdw-action-table__status" />
        <col class="pdw-action-table__notes" />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">Action</th>
          <th scope="col" class="pdw-table__metadata">Attended by</th>
          <th scope="col" class="pdw-table__metadata">Status</th>
          <th scope="col" class="pdw-table__metadata">Notes</th>
        </tr>
      </thead>
      <tbody>
        @for (row of actionsForCandidate(candidateId); track row.item.id) {
          <tr [class.pdw-table__row--excluded]="row.status === 'excluded'">
            <td data-label="Action">{{ row.item.description }}</td>
            <td data-label="Attended by" class="pdw-table__metadata">{{ roleLabels[row.item.targetRole] }}</td>
            <td data-label="Status" class="pdw-table__metadata">
              <span class="pdw-status pdw-status--{{ row.status }}">{{ actionStatusLabels[row.status] }}</span>
            </td>
            <td data-label="Notes" class="pdw-table__metadata">
              @if (workflowCase().completions[row.item.id]; as completion) {
                <div class="pdw-queue__note">{{ completion.note }}</div>
                @if (completion.reason) {
                  <div class="pdw-queue__note">Reason: {{ completion.reason }}</div>
                }
              } @else {
                <span class="pdw-cand__meta">No notes</span>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
</ng-template>

<ng-template #candidateList>
  @for (stepKey of [referenceStepKey()]; track stepKey) {
  <ul class="pdw-cands">
    @for (candidate of workflowCase().candidates; track candidate.candidateId) {
      @let actions = actionsForCandidate(candidate.candidateId);
      <li
        class="pdw-cand"
        [class.pdw-cand--selected]="selectedCandidateIds().includes(candidate.candidateId)"
        [class.pdw-cand--selectable]="canWork()">
        <details
          [open]="workflowCase().step === Step.ConfirmFindings || selectedCandidateIds().includes(candidate.candidateId)"
          class="pdw-cand__reference">
          <summary class="pdw-more__summary pdw-more__summary--disclosure">
            Candidate {{ candidate.rank }}
            <span class="pdw-panel__hint">{{ selectedCandidateIds().includes(candidate.candidateId) ? 'Selected by the analyst' : 'Not selected' }}</span>
          </summary>
          <label class="pdw-cand__label">
            <input
              [checked]="selectedCandidateIds().includes(candidate.candidateId)"
              [disabled]="!canWork() || busy()"
              (change)="toggleCandidate(candidate.candidateId)"
              type="checkbox" />
            <span class="pdw-cand__rank">{{ candidate.rank }}</span>
            <span class="pdw-cand__body">
              <span class="pdw-cand__head">
                <span class="pdw-cand__title">{{ candidate.title }}</span>
                <span class="pdw-cand__chip">{{ assessmentLabels[candidate.family] }}</span>
                <span class="pdw-cand__chip">{{ candidate.confidence }}% confidence</span>
                @if (candidate.rank === 1) {
                  <span class="pdw-cand__chip pdw-cand__chip--agent">Agent top-ranked</span>
                }
              </span>
              <span class="pdw-cand__rationale">{{ candidate.rationale }}</span>
            </span>
          </label>
          <div class="pdw-cand__actions">
            @if (actions.length) {
              <div class="pdw-cand__action-info">
                <h6 class="pdw-cand__action-heading">
                  <i class="pi pi-info-circle" aria-hidden="true"></i>
                  Recommended action{{ actions.length === 1 ? '' : 's' }}
                </h6>
                <ng-container [ngTemplateOutlet]="actionTable" [ngTemplateOutletContext]="{ $implicit: candidate.candidateId }" />
              </div>
            } @else {
              <p class="pdw-cand__meta">No recommended actions.</p>
            }
          </div>
        </details>
        @if (workflowCase().step === Step.ExecuteActions && activeCandidate()?.candidateId === candidate.candidateId) {
          <div class="pdw-cand__actions">
            <div class="pdw-cand__action-info">
              <ng-container [ngTemplateOutlet]="executeAction" />
            </div>
          </div>
        }
      </li>
    }
  </ul>
  }
  @if (additionalActions().length) {
    <section class="pdw-additional-actions" aria-label="Case-wide and other actions">
      <h5 class="pdw-subtitle">Case-wide and other actions</h5>
      <ng-container [ngTemplateOutlet]="actionTable" [ngTemplateOutletContext]="{ $implicit: null }" />
    </section>
  }
</ng-template>

@if (!store.composed()) {
  <section class="pdw" aria-label="Case workflow">
    @if (store.signInRedirect()) {
      <div class="pdw-panel" role="status" aria-live="polite">
        <p>Redirecting to sign in. If this page does not redirect, retry.</p>
        <button (click)="store.load(engineCaseId())" type="button" class="gate__btn gate__btn--primary">
          <i class="pi pi-refresh" aria-hidden="true"></i> Retry
        </button>
      </div>
    } @else if (store.loadFailed()) {
      <div class="pdw-panel" role="alert">
        <p>{{ store.caseMissing() ? 'This workflow case no longer exists.' : store.loadStatus() === 403 ? 'You do not have access to this workflow.' : 'The workflow could not be loaded. Try again.' }}</p>
        <button (click)="store.load(engineCaseId())" type="button" class="gate__btn gate__btn--primary">
          <i class="pi pi-refresh" aria-hidden="true"></i> Retry
        </button>
      </div>
    } @else {
      <p class="pdw-waiting" role="status">Loading the workflow case…</p>
    }
  </section>
} @else {
  @let wc = workflowCase();
  @let perms = permissions();
  <section class="pdw" aria-label="Case workflow">
    <header class="pdw-head">
      <div class="pdw-head__main">
        <div class="pdw-head__title-row">
          <span class="pdw-head__kind pdw-head__kind--production-deviation">Production deviation</span>
          <span class="pdw-head__state" [class.pdw-head__state--closed]="isClosed()">{{ stepLabel() }}</span>
          @if (wc.outcome; as outcome) {
            <span class="pdw-head__outcome">{{ outcomeLabels[outcome] }}</span>
          }
        </div>
        <h3 class="pdw-head__title">{{ wc.engineCaseId }}</h3>
        @if (!isClosed()) { <p class="pdw-head__owner">{{ queue() }}</p> }
        <p class="pdw-head__sub">Workflow version {{ wc.workflowVersion }}</p>
      </div>

      <dl class="pdw-head__facts">
        <div>
          <dt>Action items</dt>
          <dd>
            @if (wc.resolvedItemIds) {
              {{ actionSummary() }}
            } @else {
              Resolved on approval
            }
          </dd>
        </div>
      </dl>
    </header>


    <app-uc2-lifecycle-rail [stages]="stages()" ariaLabel="Case workflow progress" />

    @if (isClosed()) {
      <div id="pdw-current-step" class="pdw-panel pdw-panel--action">
        <h4 class="pdw-panel__title">Findings record</h4>
        <dl class="pdw-record">
          <div>
            <dt>Outcome</dt>
            <dd>{{ wc.outcome ? outcomeLabels[wc.outcome] : '—' }}</dd>
          </div>
          <div>
            <dt>Verdict</dt>
            <dd>{{ wc.findings.verdict ?? '—' }}</dd>
          </div>
          <div>
            <dt>Assessment</dt>
            <dd>{{ wc.findings.assessment ? assessmentLabels[wc.findings.assessment] : '—' }}</dd>
          </div>
          @if (wc.findings.rejectionReason; as reason) {
            <div>
              <dt>Reason for rejecting</dt>
              <dd>{{ reason }}</dd>
            </div>
          }
          <div>
            <dt>Action results</dt>
            <dd>{{ actionSummary() }}</dd>
          </div>
        </dl>
        <ng-container [ngTemplateOutlet]="commandFeedback" />
      </div>
    }

    @if (wc.step === Step.ApproveFindings || wc.step === Step.ExecuteActions) {
      <section class="pdw-panel" aria-label="Recorded analyst decision">
        <h4 class="pdw-panel__title">Analyst decision</h4>
        <dl class="pdw-record">
          <div>
            <dt>Verdict</dt>
            <dd>{{ wc.findings.verdict ?? 'Not recorded' }}</dd>
          </div>
          <div>
            <dt>Assessment</dt>
            <dd>{{ wc.findings.assessment ? assessmentLabels[wc.findings.assessment] : 'Not recorded' }}</dd>
          </div>
        </dl>
      </section>
    }

    @if (!(wc.step === Step.ConfirmFindings && canWork())) {
      <section class="pdw-panel" aria-label="Candidate explanations">
        <h4 class="pdw-panel__title">
          Candidate explanations
          <span class="pdw-panel__hint">{{ selectedCandidateIds().length ? 'Selected by the analyst' : 'Agent-proposed explanations' }}</span>
        </h4>
        <ng-container [ngTemplateOutlet]="candidateList"></ng-container>
      </section>
    }

    @if (wc.step === Step.ExecuteActions) {
      @if (!activeCandidate()) {
        <ng-container [ngTemplateOutlet]="executeAction" />
      }
    } @else if (!isClosed()) {
    <div id="pdw-current-step" class="pdw-panel pdw-panel--action">
        @switch (wc.step) {
          @case (Step.ConfirmFindings) {
            <h4 class="pdw-panel__title">{{ stepLabel() }}</h4>
            @if (wc.returnReason) {
              <p class="pdw-returned" role="status">
                <i class="pi pi-replay" aria-hidden="true"></i>
                Returned by the IO-Production-Engineer: {{ wc.returnReason }}
              </p>
            }
            @if (canWork()) {
              <p class="gate__intro">
                Choose which candidate explanations to act on and confirm the deviation. Rejecting it closes the case as dismissed.
              </p>
              @if (!perms.canConfirm) {
                <p class="pdw-claim-notice">
                  <i class="pi pi-info-circle" aria-hidden="true"></i>
                  Submitting your decision assigns this case to you. Selecting explanations and editing the form do not.
                </p>
              }
              <h5 class="pdw-subtitle">Candidate explanations <span class="pdw-panel__hint">Select the ones to act on</span></h5>
              <ng-container [ngTemplateOutlet]="candidateList"></ng-container>
              <form [formGroup]="confirmForm" [attr.aria-busy]="busy()" class="gate__form">
                <fieldset class="gate__field" [disabled]="busy()">
                  <legend class="gate__label">Verdict</legend>
                  <label class="gate__radio">
                    <input [value]="Verdict.Confirmed" type="radio" formControlName="verdict" />
                    Confirmed
                  </label>
                  <label class="gate__radio">
                    <input [value]="Verdict.Rejected" type="radio" formControlName="verdict" />
                    Rejected — not actionable
                  </label>
                </fieldset>
                @if (isRejected()) {
                  <div class="gate__field gate__field--wide">
                    <label class="gate__label" for="pdw-rejection-reason">Reason for rejecting <span class="gate__required">required</span></label>
                    <textarea
                      [formControl]="rejectionReason"
                      id="pdw-rejection-reason"
                      class="gate__control"
                      rows="3"
                      maxlength="2000"
                      placeholder="Why is this deviation not actionable?"></textarea>
                  </div>
                }
                <div class="gate__field">
                  <label class="gate__label" for="pdw-assessment">Assessment</label>
                  <select id="pdw-assessment" class="gate__control" formControlName="assessment">
                    @for (assessment of assessments; track assessment) {
                      <option [value]="assessment">{{ assessmentLabels[assessment] }}</option>
                    }
                  </select>
                  <span class="gate__muted">Agent proposed: {{ assessmentLabels[wc.proposedAssessment] }}</span>
                </div>
              </form>
              @if (confirmBlockedReason(); as blocked) {
                <p class="gate__blocked" role="status">{{ blocked }}</p>
              }
              <div class="gate__actions">
                <button
                  [disabled]="!canWork() || !!confirmBlockedReason() || busy()"
                  (click)="onConfirm()"
                  type="button"
                  class="gate__btn gate__btn--primary">
                  {{ confirmLabel() }}
                </button>
              </div>
            } @else {
              <p class="pdw-waiting">{{ waitingMessage() }}</p>
            }
          }

          @case (Step.ApproveFindings) {
            <h4 class="pdw-panel__title">{{ stepLabel() }}</h4>
            @if (perms.canApprove) {
              <p class="gate__intro">
                Approve the confirmed findings to start the actions. Return sends the case back to {{ wc.priorOwner ?? 'the analyst' }}, not the queue.
              </p>
              @if (!canApprove()) {
                <p class="gate__blocked" role="status">You hold no UC1 role that can approve these findings.</p>
              }
              <div class="gate__form">
                <div class="gate__field gate__field--wide">
                  <label class="gate__label" for="pdw-return-reason">Return reason <span class="gate__muted">(required to return)</span></label>
                  <textarea
                    [formControl]="returnReason"
                    id="pdw-return-reason"
                    class="gate__control"
                    rows="2"
                    placeholder="What does the analyst need to rework?"></textarea>
                </div>
              </div>
              <div class="gate__actions">
                <button
                  [disabled]="!canReturn() || busy()"
                  (click)="onReturn()"
                  type="button"
                  class="gate__btn gate__btn--ghost">
                  Return to analyst@if (wc.priorOwner; as name) { ({{ name }})}
                </button>
                <button
                  [disabled]="!canApprove() || busy()"
                  (click)="onApprove()"
                  type="button"
                  class="gate__btn gate__btn--primary">
                  Approve findings
                </button>
              </div>
            } @else {
              <p class="pdw-waiting">{{ waitingMessage() }}</p>
            }
          }

        }

        <ng-container [ngTemplateOutlet]="commandFeedback" />
      </div>
    }

    <details class="pdw-more" aria-label="Audit trail">
      <summary class="pdw-more__summary pdw-more__summary--disclosure">
        Audit trail
        <span class="pdw-panel__hint">Newest last, times in WAT</span>
      </summary>
      <div class="pdw-more__body">
        <app-workflow-audit-trail [trail]="auditTrail()" />
      </div>
    </details>

    @if (store.composed()?.graph; as graph) {
      <app-workflow-published-graph [graph]="graph" [nodes]="store.graph()" [actions]="graphActions()" />
    }
  </section>
}
